/**
 * Recipe Rating API Service
 *
 * With Better Auth, session cookies are sent automatically via
 * credentials: 'include'.
 */

import { apiFetch } from "./client";
import type { RatingValue } from "./types";
import { RECOMMENDATION_ATTRIBUTION_CAPABILITY, RECOMMENDATION_SCHEMA_VERSION, type RecommendationOutcomeContext, type RecommendationOutcomeReceipt } from "@batch-and-bowl/shared";

export interface RateRecipeResponse {
  value: RatingValue | null;
  ratingAverage: number;
  ratingCount: number;
  attribution?: RecommendationOutcomeReceipt;
}

export function hasRecipeOutcomeAcknowledgment(response: unknown, operationId: string): boolean {
  if (!response || typeof response !== "object" || !("attribution" in response)) return false;
  const receipt = response.attribution;
  return !!receipt && typeof receipt === "object" && "capability" in receipt && receipt.capability === RECOMMENDATION_ATTRIBUTION_CAPABILITY &&
    "schemaVersion" in receipt && receipt.schemaVersion === RECOMMENDATION_SCHEMA_VERSION &&
    "operationId" in receipt && receipt.operationId === operationId && "replayed" in receipt && typeof receipt.replayed === "boolean";
}

/**
 * Set (up/down) or clear (null) the caller's thumbs rating on a recipe.
 * Mirrors PUT /recipes/:id/rating (Task 17) -- `null` removes the rating.
 */
export async function rateRecipe(
  recipeId: string,
  value: RatingValue | null,
  outcome?: RecommendationOutcomeContext,
  expectedActorUserId?: string,
): Promise<RateRecipeResponse> {
  const response = await apiFetch<{ data: RateRecipeResponse }>(
    `/recipes/${recipeId}/rating`,
    {
      method: "PUT",
      body: JSON.stringify({ value }),
      ...(outcome || expectedActorUserId ? { headers: {
        ...(expectedActorUserId ? { "X-Expected-User-Id": expectedActorUserId } : {}),
        ...(outcome ? { "X-Recommendation-Capability": RECOMMENDATION_ATTRIBUTION_CAPABILITY, "X-Recommendation-Outcome": JSON.stringify(outcome) } : {}),
      } } : {}),
    },
  );
  return response.data;
}
